/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.0
 * Description: Login page with Microsoft Entra ID authentication
 */

import React, { useEffect, useState } from 'react'
import { AlertTriangle, LogIn } from 'lucide-react'
import { useAuth } from '@context/AuthContext'
import { toast } from 'react-toastify'

const Login: React.FC = () => {
  const { login, isLoading, authError } = useAuth()
  const [isAuthenticating, setIsAuthenticating] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('expired') === '1') {
      toast.info('Your session expired. Please sign in again.')
      // Strip the param so a refresh doesn't re-show the toast
      window.history.replaceState({}, '', '/login')
    }
  }, [])

  const handleLogin = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()

    // Prevent double-clicks
    if (isAuthenticating || isLoading) {
      return
    }

    try {
      setIsAuthenticating(true)
      await login()
      // With redirect flow, page navigates away - code below won't execute
    } catch (error: any) {
      console.error('Login error:', error)

      // Show error message
      toast.error('Login failed. Please try again.')
      setIsAuthenticating(false)
    }
  }

  return (
    <div className="from-primary-600 to-primary-800 flex min-h-screen items-center justify-center bg-gradient-to-br px-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="mb-8 text-center">
          <div className="mb-4 flex justify-center">
            <div className="rounded-full bg-white p-4 shadow-lg dark:bg-gray-800 dark:shadow-none">
              <img src="/dolly.png" alt="Dolly the Sheep" className="h-16 w-16" />
            </div>
          </div>
          <h1 className="mb-2 text-4xl font-bold text-white">DAL-ify</h1>
          <p className="text-primary-100 text-lg">Digital Abstraction Layer</p>
        </div>

        {/* Login Card */}
        <div className="rounded-lg bg-white p-8 shadow-xl dark:bg-gray-800 dark:shadow-none">
          <div className="mb-6 text-center">
            <h2 className="mb-2 text-2xl font-bold text-gray-900 dark:text-gray-100">Welcome Back</h2>
            <p className="text-gray-600 dark:text-gray-400">Sign in with your Comcast Business account</p>
          </div>

          {authError && (
            <div
              role="alert"
              className="mb-6 flex items-start space-x-3 rounded-lg border border-amber-300 bg-amber-50 p-3 dark:border-amber-700/50 dark:bg-amber-900/30"
            >
              <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600 dark:text-amber-400" />
              <p className="text-sm text-amber-800 dark:text-amber-200">{authError}</p>
            </div>
          )}

          {/* Login Button */}
          <button
            type="button"
            onClick={handleLogin}
            disabled={isLoading || isAuthenticating}
            className="btn-primary flex w-full items-center justify-center space-x-2 py-3 text-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isAuthenticating || isLoading ? (
              <>
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-white"></div>
                <span>Signing in...</span>
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                <span>Sign in with Microsoft</span>
              </>
            )}
          </button>

          {/* Internal Use Notice */}
          <div className="mt-6 rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-800/50 dark:bg-red-900/30">
            <p className="text-center text-xs font-semibold tracking-wide text-red-800 uppercase dark:text-red-300">
              Internal Use Only
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-8 text-center">
          {/* Comcast Business Logo */}
          <div className="mb-4 flex justify-center">
            <img src="/CB_Logo_Color_RGB.svg" alt="Comcast Business" className="h-12 bg-white p-2 dark:bg-gray-800" />
          </div>
          <p className="text-sm text-white">&copy; {new Date().getFullYear()} DAL-ify. All rights reserved.</p>
        </div>
      </div>
    </div>
  )
}

export default Login
